import { get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import { LayerType, TextWritingType, type EditingRange, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { pushHistory, RichTextProcessor, updateGroupLayer, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

export interface IFontStyle {
    /**
     * 设置字体 italic (font-style)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 italic
     * @param options - 配置项
     * @returns
     */
    __setItalicStyle(
        layerID: string,
        flag: boolean,
        options?: { range?: EditingRange | null },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null } | void>;

    /**
     * 设置字体 bold (font-weight)
     * @param layerID - 图层 ID
     * @param flag - 是否设置 bold
     * @param options - 配置项
     * @returns
     */
    __setBoldStyle(
        layerID: string,
        flag: boolean,
        options?: { range?: EditingRange | null },
    ): Promise<{ layer: ILayerProps; range: EditingRange | null } | void>;
}

export class FontStyle implements IFontStyle {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private calcLayout(layer: ILayerProps) {
        const { width: layerWidth, height: layerHeight } = layer;
        const layerData = layer.data as ITextLayerProps;
        const { writingMode, lineSpacing, fontSize } = layerData;

        const measureOptions = {
            keepConstantWidth: false,
            keepConstantHeight: false,
        };

        if (
            writingMode === TextWritingType.HORIZONTAL_TB &&
            (get(layer, '__fixedDirectionSize', false) ||
                checkIsHorizontalMultiLine(layerHeight, fontSize, lineSpacing))
        ) {
            measureOptions.keepConstantWidth = true;
        }

        const textSize = TextMeasurer.measure(layer, measureOptions);
        const nextLayerWidth = Math.max(layerWidth, textSize.width);
        const nextLayerHeight = Math.max(layerHeight, textSize.height);

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private async set(
        prevLayer: ILayerProps,
        kind: 'italic' | 'bold',
        flag: boolean,
        options: { range?: EditingRange | null; update?: boolean; writeHistory?: boolean } = {},
    ) {
        const { update = false, writeHistory = false } = options;
        const range = options.range || null;

        const prevLayerData = prevLayer.data as ITextLayerProps;

        let nextLayerData: ITextLayerProps = { ...prevLayerData };

        let nextRange: EditingRange | null = range;

        if (range && !range.isCollapsed) {
            const result = RichTextProcessor.process(nextLayerData, range, {
                key: kind,
                value: flag,
            });
            nextLayerData = { ...nextLayerData, textContents: result.textContents };
            nextRange = result.range;
        } else {
            const prevValue = prevLayerData[kind];

            const { textContents: prevTextContents = [] } = prevLayerData;
            if (prevTextContents.length > 0) {
                const values = prevTextContents.map((item) => item[kind]);
                const set = new Set(values);

                if (set.size === 1 && values[0] === flag) return;

                const nextTextContents = prevTextContents.map((item) => ({
                    ...item,
                    [kind]: flag,
                }));
                nextLayerData = { ...nextLayerData, textContents: nextTextContents };
            } else {
                if (prevValue === flag) return;
                nextLayerData = { ...nextLayerData, [kind]: flag };
            }
        }

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayout(nextLayer);
            nextLayer.width = nextLayerWidth;
            nextLayer.height = nextLayerHeight;
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer);
        if (writeHistory) pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER][kind]);

        return {
            layer: nextLayer,
            range: nextRange,
        };
    }

    private async setFontStyle(
        layerID: string,
        kind: 'italic' | 'bold',
        flag: boolean,
        options = { range: null, writeHistory: true },
    ) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const results = await Promise.all(children.map((child) => this.set(child, kind, flag, options)));
            const layers = results.map((result) => (result ? result.layer : null)).filter(Boolean) as ILayerProps[];

            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER][kind],
                update: true,
                writeHistory: options.writeHistory,
            });

            return { layer: nextLayer, range: null };
        }

        const { range } = options;
        return this.set(prevLayer, kind, flag, { range, update: true, writeHistory: options.writeHistory });
    }

    __setItalicStyle(layerID: string, flag: boolean, options = { range: null, writeHistory: true }) {
        return this.setFontStyle(layerID, 'italic', flag, options);
    }

    __setBoldStyle(layerID: string, flag: boolean, options = { range: null, writeHistory: true }) {
        return this.setFontStyle(layerID, 'bold', flag, options);
    }
}
